import { getI18n } from "../i18n";
import { CaseDocument, CaseList, GenCase, WebCase, WebsiteForm } from "../models";
import { RAGType } from "../models/cases/LLMCase";
import { ANALYSIS_RESULT } from "../models/websitecheck";
import { ReportConfig } from "../models/websitecheck/Report";
import { showNotification } from "../service/notification";
import { ServerName, ReportEndpoint } from "../utils/constants/serverName";
import axios from "../utils/http/axios";
import { convertRangeToDate, FilterDateRange } from "../utils/time/TimeDateConverter";
import { getCategoryFromDocumentType } from "../utils/workarounds/documentTypes";
import { snakeToCamel } from "../utils/workarounds/snakeCase";

export const getWebsiteChecks = async () => {
  try {
    const response = await axios.get<WebCase[]>(ServerName + "/case?type=web");
    return await Promise.resolve(response.data.sort((a, b) => b.creation_date - a.creation_date));
  } catch (err) {
    return await Promise.reject(err);
  }
};

export const getWebsiteCheckReport = async (caseId: string, config: null | ReportConfig = null) => {
  try {
    const result = await axios.post(
      `${ReportEndpoint}/report/` + caseId,
      { config: config },
      {
        responseType: "arraybuffer",
      }
    );

    const blob = new Blob([result.data]);
    return Promise.resolve(blob);
  } catch (error: any) {
    return Promise.reject(error);
  }
};
export const getDefaultReportConfigs = async () => {
  try {
    const result = await axios.get<ReportConfig[]>(`${ServerName}/reportconfig`);

    return Promise.resolve(result.data);
  } catch (error: any) {
    return Promise.reject(error);
  }
};
export const getReportConfigs = async (tenantId: string) => {
  try {
    const result = await axios.get<ReportConfig[]>(`${ServerName}/reportconfig/${tenantId}`);

    return Promise.resolve(result.data);
  } catch (error: any) {
    return Promise.reject(error);
  }
};
export const getWebsiteCheckResult = async (caseId: string) => {
  try {
    const result = await axios.get<ANALYSIS_RESULT>(`${ServerName}/result/` + caseId);

    return Promise.resolve(result.data);
  } catch (error: any) {
    return Promise.reject(error);
  }
};
export const getCasesForType = async (caseType: string, params?: URLSearchParams): Promise<CaseList> => {
  try {
    const urlParams = params ? params : new URLSearchParams();
    urlParams.append("type", caseType);

    const response = await axios.get<CaseList>(`${ServerName}/case`, { params: urlParams });
    return await Promise.resolve(response.data);
  } catch (err) {
    return await Promise.reject(err);
  }
};

export const getCSV = async (caseIds: string[], caseType: string, separator: string, withQuoteChar: boolean) => {
  try {
    const params = new URLSearchParams();
    params.append("type", caseType);
    params.append("separator", separator);
    params.append("withQuoteChar", withQuoteChar.toString());

    const response = await axios.post<string>(ServerName + `/export?type=${caseType}`, caseIds, {
      params: params,
      responseType: "arraybuffer",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const blob = new Blob([response.data]);
    return await Promise.resolve(blob);
  } catch (err) {
    return await Promise.reject(err);
  }
};

export const createCase = async (data: { url: string; client_email: string; type: string; state: "created" | "increation"; description?: string; title?: string; tenant: number }) => {
  try {
    const result = await axios.post<WebCase>(`${ServerName}/case`, data);
    return await Promise.resolve(result);
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const createEmptyCase = async (data: { type: string; state: string; title: string; rag_type: RAGType; index_location?: string; index_id?: string }) => {
  try {
    const result = await axios.post<GenCase>(`${ServerName}/case`, data);
    return await Promise.resolve(result.data);
  } catch (error) {
    return await Promise.reject(error);
  }
};
interface FileItem {
  uid: string;
  name?: string;
  status?: string;
  response?: string;
  url?: string;
  preview?: string;
  originFileObj: File;
  file: string | Blob;
}
export const addWebsiteCheck = async (url: string, name: string, crawlDepth: string, workflow: string, schedule: string, agbRelevant: boolean, email: string, tenantId: number) => {
  const newCase = {
    url: url,
    name_website: name,
    relation: schedule,
    crawl_depth: crawlDepth,
    workflow_type: workflow,
    type: "web",
    client_email: email,
    state: "created",
    tenant: tenantId,
    agb_relevant: agbRelevant,
    language: "DE",
  };

  try {
    newCase.workflow_type = "DEFAULT";
    const result = await axios.post<WebCase>(`${ServerName}/case`, newCase);
    return await Promise.resolve(result);
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const addNewDocument = async (caseId: string, url: string, docType: string, content = "") => {
  const urlEncoded = encodeURIComponent(url);
  const category = getCategoryFromDocumentType(docType);
  const formData = new FormData();
  if (content != "") {
    const blobData = new Blob([content], { type: "text/html" });
    formData.append("files", blobData);
  }
  let endpoint = `${ServerName}/case/${caseId}/document?fieldname=${category}&type=${docType}&state=new`;
  if (url != null && url !== "") {
    endpoint += `&url=${urlEncoded}`;
  }
  try {
    const doc = await axios.post<CaseDocument[]>(endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return Promise.resolve(doc.data);
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const addDocumentToCase = async (caseId: string, url: string, docType: string, file: FileItem) => {
  const urlEncoded = encodeURIComponent(url);
  const category = "documents";
  const formData = new FormData();
  if (file) {
    formData.append("files", file.originFileObj, file.name);
  }
  let endpoint = `${ServerName}/case/${caseId}/document?fieldname=${category}&type=${docType}&state=new`;
  if (url != null && url !== "") {
    endpoint += `&url=${urlEncoded}`;
  }
  try {
    const doc = await axios.post<CaseDocument[]>(endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return Promise.resolve(doc.data);
  } catch (error) {
    return await Promise.reject(error);
  }
};
export const updateCase = async (gencase: Partial<GenCase>) => {
  try {
    if (!gencase.id) {
      return Promise.reject();
    }
    await axios.patch(`${ServerName}/case/${gencase.id}`, gencase);
    return await Promise.resolve();
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const restartCaseWorkflow = async (caseId: string, state: string, type = "web") => {
  try {
    await axios.patch(`${ServerName}/case/${caseId}`, {
      id: caseId,
      type: type,
      state: state,
    });
    return await Promise.resolve();
  } catch (error) {
    return await Promise.reject(error);
  }
};
export const updateForm = async (form: WebsiteForm) => {
  try {
    const formData = new FormData();
    formData.append("form", JSON.stringify(form));
    await axios.put(`${ServerName}/document/${form.document.id}/form`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    return Promise.resolve();
  } catch {
    return Promise.reject();
  }
};

export const addFormToCase = async (webCase: WebCase, url: string, formType: string) => {
  try {
    const blobData = new Blob([""], { type: "text/html" });

    const formData = new FormData();
    const params = new URLSearchParams();
    formData.append("type", formType);
    formData.append("url", encodeURIComponent(url));

    await axios.post(`${ServerName}/case/${webCase.id}/form`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return Promise.resolve();
  } catch {
    return Promise.reject();
  }
};

export const getAllCasesPaged = async (urlParams: URLSearchParams, overrideCaseType?: string): Promise<CaseList> => {
  const params = Object.fromEntries(urlParams);
  for (const key in params) {
    if (!params[key]) {
      delete params[key];
    } else {
      let val = params[key];
      if (!val || val == null || val === "null" || val == "" || val === "undefined") {
        delete params[key];
      }
    }
  }
  if (params["page"]) {
    params["page"] = (parseInt(params["page"]) - 1).toString();
  }
  if (params["sortBy"]) {
    params["sortBy"] = snakeToCamel(params["sortBy"]);
  }
  if (overrideCaseType) {
    params["type"] = overrideCaseType;
  }
  if (params["dateCreated"]) {
    const dateVal = params["dateCreated"] || "last30days";
    const { firstDay, lastDay } = convertRangeToDate(dateVal as FilterDateRange);
    delete params["dateCreated"];
    params["fromDate"] = firstDay.toISOString();
    params["toDate"] = lastDay.toISOString();
  }

  try {
    const response = await axios.get<CaseList>(`${ServerName}/case`, { params: params });
    response.data.content.forEach((caseDoc) => {
      caseDoc.type = caseDoc.type == null ? "-" : caseDoc.type;
    });
    return Promise.resolve(response.data);
  } catch (err) {
    const t = getI18n().global.t;
    showNotification("error", t("common.error.title"), t("error.documentlist.load"));
    return Promise.reject(err);
  }
};
